import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

export interface LeadEmail {
  id: string;
  lead_id: string;
  resend_email_id: string;
  from_address: string;
  from_name: string | null;
  to_addresses: string[];
  cc_addresses: string[];
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  attachments: Array<{
    filename: string;
    content_type: string;
    size: number;
  }>;
  received_at: string;
  created_at: string;
  is_read: boolean;
  replied_at: string | null;
}

export const useLeadEmails = (leadId: string) => {
  const queryClient = useQueryClient();

  const { data: emails, isLoading, error } = useQuery({
    queryKey: ["lead-emails", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lead_emails")
        .select("*")
        .eq("lead_id", leadId)
        .order("received_at", { ascending: false });

      if (error) throw error;
      return data as LeadEmail[];
    },
    enabled: !!leadId,
  });

  // Set up realtime subscription
  useEffect(() => {
    if (!leadId) return;

    const channel = supabase
      .channel(`lead-emails-${leadId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "lead_emails",
          filter: `lead_id=eq.${leadId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["lead-emails", leadId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [leadId, queryClient]);

  const markAsReadMutation = useMutation({
    mutationFn: async (emailId: string) => {
      const { error } = await supabase
        .from("lead_emails")
        .update({ is_read: true })
        .eq("id", emailId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-emails", leadId] });
    },
  });

  const markAsRepliedMutation = useMutation({
    mutationFn: async (emailId: string) => {
      const { error } = await supabase
        .from("lead_emails")
        .update({ replied_at: new Date().toISOString() })
        .eq("id", emailId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-emails", leadId] });
    },
  });

  const unreadCount = emails?.filter(e => !e.is_read).length || 0;

  return {
    emails: emails || [],
    isLoading,
    error,
    unreadCount,
    markAsRead: markAsReadMutation.mutate,
    markAsReplied: markAsRepliedMutation.mutate,
  };
};
